/**
 * Handles i18n translation logic for the login page. Updates static text elements
 * on the page by manipulating the DOM based on the user-selected language using LinguiJS.
 *
 * - Loads and applies translations dynamically.
 * - Updates DOM elements with the appropriate localized text.
 *
 * @author Akash Salvi <akash.salvi@helpshift.com>
 * @created April 28, 2025
 * @module pages/internationalization/login
 */

import dom from "dom";
import {msg, t} from "@lingui/core/macro";
import {updateFooter} from "pages/internationalization/internationalizationLogic";

const EMAIL_INPUT_LABEL_TEXT = msg`Email Address`;
const FORGET_PASSWORD_TEXT = msg`Forgot Password`;
const EMAIL_INPUT_PLACEHOLDER_TEXT = msg`Enter email address`;
const EMAIL_REQUIRED_TEXT = msg`Email address is a required field`;
const EMAIL_INVALID_TEXT = msg`Please enter a valid email address`;
const PASSWORD_INPUT_LABEL_TEXT = msg`Password`;

/**
 * Mapping of element selectors to translated text strings for various Login Page UI parts
 * such as headers, button text, labels, and SSO options.
 */
const TEXT_TRANSLATION_MAP = {
  // Headers, sub-text and button text
  "#login-page-header": msg`Log in to your account`,
  "#forget-password-header": FORGET_PASSWORD_TEXT,
  "#forget-password-description": msg`Enter the email address you use to log in with, and we will send you a link to reset your password.`,
  "#login-submit-button": msg`Log In To My Account`,
  "#send-reset-link-btn": msg`Send Reset Link`,
  "#forgot-password-link": FORGET_PASSWORD_TEXT,
  "#back-to-login-link": msg`Back to Login`,
  "#other-login-header": msg`or login with:`,
  "#single-sign-on": msg`Single Sign-On`,
  "#single-login-title": msg`Log in to your account`,

  // Login form labels
  "#passowrd-formfield-label": PASSWORD_INPUT_LABEL_TEXT,
  "#remember-me-option-label": msg`Remember me on this computer for 7 days`,
  "#field-captcha-label": msg`Security Text`,

  // Single-Sign on Options
  "#enterprise-login-option": msg`Enterprise login`
};

/**
 * Mapping of input element selectors to placeholder text translations.
 */
const INPUT_PLACEHOLDERS_MAP = {
  "#username": EMAIL_INPUT_PLACEHOLDER_TEXT,
  "#password": msg`Enter password`,
  "#email-address": EMAIL_INPUT_PLACEHOLDER_TEXT,
  "#field-captcha-input": msg`Enter shown text`
};

/**
 * Mapping of input element selectors to title text translations.
 */
const TITLE_ARGS_MAP = {
  "#username": EMAIL_INPUT_LABEL_TEXT,
  "#password": PASSWORD_INPUT_LABEL_TEXT,
  "#refresh-captcha-btn": msg`Reload security text`,
  "#email-address": EMAIL_INPUT_LABEL_TEXT
};

/**
 * Mapping of error message element selectors to translated error messages.
 */
const ERROR_MESSAGE_MAP = {
  "#username-required": EMAIL_REQUIRED_TEXT,
  "#username-invalid": EMAIL_INVALID_TEXT,
  "#forgot-email-error": EMAIL_REQUIRED_TEXT,
  "#forgot-email-invalid": EMAIL_INVALID_TEXT,
  "#password-required": msg`Password is a required field`,
  "#captcha-required": msg`Security text is a required field`,
  "#captcha-invalid": msg`Security text doesn't match. Try again`,
  "#captcha-expired": msg`Security text expired. Try again`,
  "#password-error": msg`Invalid Email/Password`
};

/**
 * Error messages map used in validation logic.
 */
export const errorMsgs = {
  requiredEmail: EMAIL_REQUIRED_TEXT,
  requiredPassword: ERROR_MESSAGE_MAP["#password-required"],
  requiredCaptcha: ERROR_MESSAGE_MAP["#captcha-required"],
  validEmail: EMAIL_INVALID_TEXT
};

/**
 * Updates all login page UI elements with the appropriate translated text based on
 * the current language selection.
 *
 * NOTE: The backend creates the login page HTML, so we can’t include translations
 * directly when the page is rendered.
 * Therefore, we use DOM manipulation to replace the static text with the
 * user’s preferred language translation after the page loads.
 */
export const updateLoginPageLanguage = () => {
  updateFooter();
  updateTitleArgsContent();

  Object.keys(TEXT_TRANSLATION_MAP).map((elementId) => {
    const textEl = dom.one(elementId);

    if (textEl) {
      textEl.getDOM().innerText = t(TEXT_TRANSLATION_MAP[elementId]);
    }
  });

  Object.keys(INPUT_PLACEHOLDERS_MAP).map((elementId) => {
    const placeholderEl = dom.one(elementId);

    if (placeholderEl) {
      placeholderEl.getDOM().placeholder = t(INPUT_PLACEHOLDERS_MAP[elementId]);
    }
  });

  const emailInputLabel = document.querySelectorAll("#email-formfield-label");

  if (emailInputLabel.length > 1) {
    emailInputLabel[0].innerText = t(EMAIL_INPUT_LABEL_TEXT);
    emailInputLabel[1].innerText = t(EMAIL_INPUT_LABEL_TEXT);
  }
};

const updateTitleArgsContent = () => {
  Object.keys(TITLE_ARGS_MAP).map((elementId) => {
    const element = dom.one(elementId);

    if (element) {
      element.getDOM().title = t(TITLE_ARGS_MAP[elementId]);
    }
  });
};

/**
 * Updates visible error message elements on the page with their translated
 * versions based on the current language.
 *
 * Only updates error messages that are currently visible (i.e., display style not "none").
 */
export const updateErrorMsg = () => {
  Object.keys(ERROR_MESSAGE_MAP).map((errorElementId) => {
    const errorElement = dom.one(errorElementId);

    if (errorElement && errorElement.getDOM().style.display !== "none") {
      errorElement.getDOM().innerText = t(ERROR_MESSAGE_MAP[errorElementId]);
    }
  });
};
